import { useState } from 'react'
import { SourceProps, LayerProps } from 'react-map-gl'
import { SymbolLayer } from 'mapbox-gl'

import { paint, placesLayout, priority } from './common'

import type { CircleLayer, Expression, GeoJSONSourceRaw } from 'mapbox-gl'

const booksRouteURL = process.env.REACT_APP_BOOKS_ROUTE_URL
const bookEndpoint = 'bookdata/api/public/getUserGeneratedBookDataGeoJson/'

declare type GeoJSONSourceProps = GeoJSONSourceRaw & {
  id?: string
}

export const userGeneratedLocationsSource: GeoJSONSourceProps = {
  id: 'user-generated-locations',
  type: 'geojson',
  data: `${booksRouteURL}${bookEndpoint}`,
  generateId: true,
  cluster: true,
  clusterRadius: 30,
  clusterMaxZoom: 16,
  clusterProperties: {
    clusterName: [
      [
        'case',
        ['==', ['accumulated'], ['get', 'clusterName']],
        ['get', 'clusterName'],
        null
      ],
      ['get', 'name']
    ]
  }
}

export const userGeneratedLocationsLayerStyle: SymbolLayer = {
  id: 'user-generated-locations',
  type: 'symbol',
  source: 'user-generated-locations',
  filter: ['!', ['has', 'point_count']],
  layout: placesLayout,
  paint: paint
}

interface UseLayerPropsState {
  userGeneratedLocationsLayerStyle: LayerProps
  toggleUserGeneratedLocationsLayerOpacity: (e?: boolean) => void
  layerVisibility: boolean
  setLayerVisibility: (layerVisibility: boolean) => void
  toggleUserGeneratedLocationsLayerVisibility: (e?: boolean) => void
}

export const useUserGeneratedLocationsLayerStyle = (): UseLayerPropsState => {
  const [layerVisibility, setLayerVisibility] = useState(true)
  const [layerStyle, setLayerStyle] = useState<SymbolLayer>(
    userGeneratedLocationsLayerStyle
  )

  const toggleOpacity = (isHighLight?: boolean) => {
    if (isHighLight) {
      setLayerStyle(state => ({
        ...state,
        paint: {
          ...state.paint,
          'icon-opacity': ['match', ['feature-state', 'active'], 1, 1, 0.5]
        }
      }))
    } else {
      setLayerStyle(state => ({
        ...state,
        paint: {
          ...state.paint,
          'icon-opacity': 1
        },
        layout: {
          ...state.layout,
          'symbol-sort-key': priority as Expression
        }
      }))
    }
  }

  const toggleLayerVisibility = (isVisible?: boolean) => {
    setLayerStyle(state => ({
      ...state,
      layout: {
        ...state.layout,
        visibility: isVisible ? ('visible' as const) : ('none' as const)
      }
    }))
  }
  return {
    userGeneratedLocationsLayerStyle: layerStyle,
    toggleUserGeneratedLocationsLayerOpacity: toggleOpacity,
    layerVisibility,
    setLayerVisibility,
    toggleUserGeneratedLocationsLayerVisibility: toggleLayerVisibility
  }
}

export const uglClusterCountLayerStyle: SymbolLayer = {
  id: 'ugl-cluster-count',
  type: 'symbol',
  source: 'user-generated-locations',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': [
      'coalesce',
      ['get', 'clusterName'],
      ['get', 'point_count_abbreviated']
    ],
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 14
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': 'rgba(255,255,255,0.9)',
    'text-halo-width': 1.5,
    'text-halo-blur': 0
  }
}

interface UseClusterCountLayerPropsState {
  layerStyle: LayerProps
  layerVisibility: boolean
  setLayerVisibility: (layerVisibility: boolean) => void
  toggleLayerVisibility: (e?: boolean) => void
}

export const useUglClusterCountLayerStyle =
  (): UseClusterCountLayerPropsState => {
    const [layerVisibility, setLayerVisibility] = useState(true)
    const [layerStyle, setLayerStyle] = useState<SymbolLayer>(
      uglClusterCountLayerStyle
    )

    const toggleLayerVisibility = (isVisible?: boolean) => {
      setLayerStyle(state => ({
        ...state,
        layout: {
          ...state.layout,
          visibility: isVisible ? ('visible' as const) : ('none' as const)
        }
      }))
    }

    return {
      layerStyle: layerStyle,
      layerVisibility,
      setLayerVisibility,
      toggleLayerVisibility
    }
  }

export const uglClusterLayerStyle: CircleLayer = {
  id: 'ugl-clusters',
  type: 'circle',
  source: 'user-generated-locations',
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': [
      'step',
      ['get', 'point_count'],
      'rgba(72, 202, 228, 0.85)',
      100,
      'rgba(72, 202, 228, 0.90)',
      750,
      'rgba(72, 202, 228, 0.95)'
    ],
    'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40]
  }
}

interface UseUglClusterLayerPropsState {
  layerStyle: LayerProps
  layerVisibility: boolean
  setLayerVisibility: (layerVisibility: boolean) => void
  toggleLayerVisibility: (e?: boolean) => void
}

export const useUglClusterLayerStyle = (): UseUglClusterLayerPropsState => {
  const [layerVisibility, setLayerVisibility] = useState(true)
  const [layerStyle, setLayerStyle] =
    useState<CircleLayer>(uglClusterLayerStyle)

  const toggleLayerVisibility = (isVisible?: boolean) => {
    setLayerStyle(state => ({
      ...state,
      layout: {
        ...state.layout,
        visibility: isVisible ? ('visible' as const) : ('none' as const)
      }
    }))
  }

  return {
    layerStyle: layerStyle,
    layerVisibility,
    setLayerVisibility,
    toggleLayerVisibility
  }
}
